import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Image, Pencil, Trash2, Lock, CheckCircle2, HelpCircle, History, Send } from 'lucide-react';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { Textarea } from '../ui/textarea';
import { formatDateTime, formatDate } from '../../lib/dateUtils';
import { API_URL, auth, errMsg, rand, categoryMeta } from './financeApi';

const ReviewBadge = ({ status }) => {
  if (status === 'checked') return <Badge className="bg-emerald-600" data-testid="expense-review-checked"><CheckCircle2 className="w-3 h-3 mr-1" />Checked</Badge>;
  if (status === 'query') return <Badge className="bg-amber-500" data-testid="expense-review-query"><HelpCircle className="w-3 h-3 mr-1" />Query</Badge>;
  return null;
};

const fieldValue = (c, v) => (c.field === 'amount' ? rand(v) : v);

const Thread = ({ e }) => (e.messages.length > 0 && (
  <ul className="space-y-1.5 border-l-2 border-amber-200 pl-3" data-testid={`expense-thread-${e.expense_id}`}>
    {e.messages.map((m, i) => (
      <li key={i} className="text-xs">
        <span className="font-semibold">{m.by_name}</span>
        <span className="text-muted-foreground"> · {m.kind === 'reply' ? 'reply' : m.kind} · {formatDateTime(m.at)}</span>
        <p className="text-sm">{m.message}</p>
      </li>
    ))}
  </ul>
));

const EditLog = ({ e }) => (e.edit_log.length > 0 && (
  <details className="text-xs" data-testid={`expense-editlog-${e.expense_id}`}>
    <summary className="cursor-pointer text-muted-foreground flex items-center gap-1"><History className="w-3 h-3" /> Changed {e.edit_log.length} time(s)</summary>
    <ul className="mt-1 space-y-1">
      {e.edit_log.map((l, i) => (
        <li key={i}>{formatDateTime(l.at)} · {l.by_name}: {l.changes.map((c) => `${c.field} ${fieldValue(c, c.old)} → ${fieldValue(c, c.new)}`).join(', ')}</li>
      ))}
    </ul>
  </details>
));

const OperatorActions = ({ e, token, onDone }) => {
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const send = async (status) => {
    setBusy(true);
    try {
      const res = await axios.put(`${API_URL}/finance/expenses/${e.expense_id}/review`, { status, note }, auth(token));
      toast.success(res.data.message);
      setNote('');
      onDone();
    } catch (err) {
      toast.error(errMsg(err, 'Could not update'));
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="space-y-2">
      <Textarea rows={2} placeholder="Add a note (needed for a query)" value={note} onChange={(ev) => setNote(ev.target.value)} data-testid={`review-note-${e.expense_id}`} />
      <div className="flex gap-2">
        <Button size="sm" disabled={busy} className="rounded-full bg-emerald-600 hover:bg-emerald-700" onClick={() => send('checked')} data-testid={`review-checked-${e.expense_id}`}><CheckCircle2 className="w-4 h-4 mr-1" /> Checked</Button>
        <Button size="sm" disabled={busy || !note.trim()} variant="outline" className="rounded-full border-amber-300 text-amber-800" onClick={() => send('query')} data-testid={`review-query-${e.expense_id}`}><HelpCircle className="w-4 h-4 mr-1" /> Query</Button>
      </div>
    </div>
  );
};

const ReplyBox = ({ e, token, onDone }) => {
  const [text, setText] = useState('');
  const send = async () => {
    try {
      await axios.post(`${API_URL}/finance/expenses/${e.expense_id}/reply`, { message: text }, auth(token));
      toast.success('Reply sent');
      setText('');
      onDone();
    } catch (err) {
      toast.error(errMsg(err, 'Could not send reply'));
    }
  };
  return (
    <div className="flex gap-2">
      <Textarea rows={1} placeholder="Reply to your operator" value={text} onChange={(ev) => setText(ev.target.value)} data-testid={`reply-input-${e.expense_id}`} />
      <Button size="icon" disabled={!text.trim()} onClick={send} data-testid={`reply-send-${e.expense_id}`}><Send className="w-4 h-4" /></Button>
    </div>
  );
};

export const ExpenseCard = ({ e, role, token, onEdit, onDelete, onViewSlip, onDone }) => {
  const meta = categoryMeta(e.category);
  const Icon = meta.icon;
  const operatorReviewing = role === 'transport' && !e.mine;
  return (
    <article className="bg-white border rounded-2xl p-4 space-y-3" data-testid={`expense-card-${e.expense_id}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <span className={`w-10 h-10 rounded-xl flex items-center justify-center shrink-0 ${meta.color}`}><Icon className="w-5 h-5" /></span>
          <div className="min-w-0">
            <p className="font-heading font-bold text-lg leading-tight" data-testid={`expense-amount-${e.expense_id}`}>{rand(e.amount)} <span className="text-sm font-normal text-muted-foreground">{meta.label}</span></p>
            <p className="text-xs text-muted-foreground">{formatDate(e.date)} · {e.registration_number}{role === 'transport' ? ` · ${e.mine ? 'You' : e.author_name}` : ''}</p>
            <p className="text-xs text-muted-foreground">Saved {formatDateTime(e.created_at)}{e.source === 'trial' ? ' · trial' : ''}</p>
          </div>
        </div>
        <div className="flex flex-col items-end gap-1">
          <ReviewBadge status={e.review_status} />
          {e.mine && !e.can_edit && <span className="text-[11px] text-muted-foreground flex items-center gap-1" data-testid={`expense-locked-${e.expense_id}`}><Lock className="w-3 h-3" /> Locked</span>}
        </div>
      </div>
      <div className="flex flex-wrap gap-2">
        {e.has_slip && <Button size="sm" variant="outline" className="rounded-full" onClick={() => onViewSlip(e)} data-testid={`expense-slip-${e.expense_id}`}><Image className="w-4 h-4 mr-1" /> Slip</Button>}
        {e.can_edit && (
          <>
            <Button size="sm" variant="outline" className="rounded-full" onClick={() => onEdit(e)} data-testid={`expense-edit-${e.expense_id}`}><Pencil className="w-4 h-4 mr-1" /> Edit</Button>
            <Button size="sm" variant="outline" className="rounded-full text-red-600 border-red-200" onClick={() => onDelete(e)} data-testid={`expense-delete-${e.expense_id}`}><Trash2 className="w-4 h-4 mr-1" /> Delete</Button>
            <span className="text-[11px] text-muted-foreground self-center">Editable until {formatDateTime(e.editable_until)}</span>
          </>
        )}
      </div>
      <Thread e={e} />
      {e.mine && e.review_status === 'query' && <ReplyBox e={e} token={token} onDone={onDone} />}
      {operatorReviewing && <OperatorActions e={e} token={token} onDone={onDone} />}
      <EditLog e={e} />
    </article>
  );
};
